import React, { useState, FC } from 'react';
import {
  Box,
  InputLabel,
  Button,
  Popover,
  TextField,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore as ChevronDownIcon } from '@mui/icons-material';

export type FilterOptionValue = string | number;

interface IFilterOptionObject {
	value?: FilterOptionValue;
	label?: string;
	[key: string]: any;
}

export type FilterOption = string | IFilterOptionObject;

interface IFilterDropdownProps {
	label: string;
	placeholder: string;
	options: FilterOption[];
	selectedItems: FilterOptionValue[];
	onToggleSelection: (value: FilterOptionValue) => void;
	displayKey?: string | null;
	disabled?: boolean;
	onSelectAll?: ((filteredOptions: FilterOption[]) => void) | null;
}

const FilterDropdown: FC<IFilterDropdownProps> = ({
  label,
  placeholder,
  options,
  selectedItems,
  onToggleSelection,
  displayKey = null,
  disabled = false,
  onSelectAll = null,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchValue('');
  };

  const open = Boolean(anchorEl) && !disabled;

  const filteredOptions = options.filter((option) => {
    const searchText =
      typeof option === 'string' 
        ? option 
        : displayKey 
          ? (option as IFilterOptionObject)[displayKey] 
          : (option as IFilterOptionObject).label || String(option);
    return String(searchText).toLowerCase().includes(searchValue.toLowerCase());
  });

  // Check if all filtered options are selected
  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every((option) => {
    const value: FilterOptionValue = typeof option === 'string' 
      ? option 
      : (option as IFilterOptionObject).value ?? String(option);
    return selectedItems.includes(value);
  });

  // Check if some (but not all) filtered options are selected
  const someFilteredSelected = filteredOptions.some((option) => {
    const value: FilterOptionValue = typeof option === 'string' 
      ? option 
      : (option as IFilterOptionObject).value ?? String(option);
    return selectedItems.includes(value);
  });

  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll(filteredOptions);
    }
  };

  return (
    <Box>
      <InputLabel sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>{label}</InputLabel>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleClick}
        endIcon={<ChevronDownIcon />}
        disabled={disabled}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          textAlign: 'left',
          color: selectedItems.length > 0 ? 'text.primary' : 'text.secondary',
          borderColor: disabled ? 'action.disabled' : 'divider',
          '&:hover': {
            borderColor: disabled ? 'action.disabled' : 'primary.main',
            bgcolor: disabled ? 'transparent' : 'action.hover',
          },
        }}
      >
        {selectedItems.length > 0
          ? `${selectedItems.length} ${label.toLowerCase()}(s) selected`
          : placeholder}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 400, maxWidth: '90vw' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {/* Select All Option */}
            {onSelectAll && filteredOptions.length > 0 && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleSelectAll}
                    dense
                    sx={{
                      bgcolor: allFilteredSelected ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected && !allFilteredSelected}
                      size="small"
                    />
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: allFilteredSelected || someFilteredSelected ? 600 : 400,
                          }}
                        >
                          Select All
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}

            {/* Individual Options */}
            {filteredOptions.map((option, idx) => {
              const value: FilterOptionValue = typeof option === 'string' 
                ? option 
                : (option as IFilterOptionObject).value ?? String(option);
              const display =
                typeof option === 'string' 
                  ? option 
                  : displayKey 
                    ? (option as IFilterOptionObject)[displayKey] 
                    : (option as IFilterOptionObject).label || String(option);
              const isSelected = selectedItems.includes(value);

              return (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      onToggleSelection(value);
                    }}
                    dense
                  >
                    <Checkbox checked={isSelected} size="small" />
                    <ListItemText
                      primary={display}
                      sx={{
                        fontFamily:
                          typeof option === 'object' &&
                          (option as IFilterOptionObject).value &&
                          String((option as IFilterOptionObject).value).startsWith('MH')
                            ? 'monospace'
                            : 'inherit',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {filteredOptions.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No items found
                </Typography>
              </Box>
            )}
          </List>

          {selectedItems.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">
                  {selectedItems.length} selected
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterDropdown;
